import type { MantineTheme } from "@mantine/core";

/**
 * Utility functions for consistent color usage across light and dark themes
 *
 * These utilities help maintain consistent color schemes throughout the application,
 * ensuring that all components use the same color combinations for positive/negative states,
 * errors, and success indicators across both light and dark modes.
 *
 * @example
 * ```tsx
 * // Usage in a component
 * import { getTrendColor } from "../utils/theme-colors";
 *
 * function MyComponent({ isPositive }: { isPositive: boolean }) {
 *   return (
 *     <Text sx={(theme) => ({ color: getTrendColor(theme, isPositive) })}>
 *       {isPositive ? "+5.2%" : "-2.1%"}
 *     </Text>
 *   );
 * }
 * ```
 */

/**
 * Returns the appropriate green color based on the current color scheme
 * Used for positive states, success indicators, and upward trends
 *
 * @param theme - Mantine theme object
 * @returns Color string - green[4] for dark mode, green[6] for light mode
 */
export function getPositiveColor(theme: MantineTheme): string {
  return theme.colorScheme === "dark" ? theme.colors.green[4] : theme.colors.green[6];
}

/**
 * Returns the appropriate red color based on the current color scheme
 * Used for negative states, error indicators, and downward trends
 *
 * @param theme - Mantine theme object
 * @returns Color string - red[1] for dark mode, red[5] for light mode
 */
function getNegativeColor(theme: MantineTheme): string {
  return theme.colorScheme === "dark" ? theme.colors.red[1] : theme.colors.red[5];
}

/**
 * Returns the appropriate error color based on the current color scheme
 * Used specifically for error states and error buttons
 *
 * @param theme - Mantine theme object
 * @returns Color string - red[1] for dark mode, red[5] for light mode
 */
export function getErrorColor(theme: MantineTheme): string {
  return theme.colorScheme === "dark" ? theme.colors.red[1] : theme.colors.red[5];
}

/**
 * Returns the appropriate success color based on the current color scheme
 * Used for connection indicators and success states
 *
 * @param theme - Mantine theme object
 * @returns Color string - green[4] for dark mode, green[6] for light mode
 */
export function getSuccessColor(theme: MantineTheme): string {
  return theme.colorScheme === "dark" ? theme.colors.green[4] : theme.colors.green[6];
}

/**
 * Helper function to get color based on positive/negative state
 * Convenience function that combines getPositiveColor and getNegativeColor
 *
 * @param theme - Mantine theme object
 * @param isPositive - Whether the state is positive (true) or negative (false)
 * @returns Color string - appropriate green or red color based on state and theme
 *
 * @example
 * ```tsx
 * // For displaying trend indicators
 * <Icon
 *   icon={isUp ? "trending_up" : "trending_down"}
 *   sx={(theme) => ({ color: getTrendColor(theme, isUp) })}
 * />
 * ```
 */
export function getTrendColor(theme: MantineTheme, isPositive: boolean): string {
  return isPositive ? getPositiveColor(theme) : getNegativeColor(theme);
}

/**
 * GREY TEXT COLOR UTILITIES
 *
 * These utilities provide consistent grey text colors across light and dark modes
 * for different text hierarchy levels and use cases.
 */

/**
 * Returns the primary text color (highest contrast)
 * Used for main headings, important text, and primary content
 *
 * @param theme - Mantine theme object
 * @returns Color string - white for dark mode, black for light mode
 */
export function getPrimaryTextColor(theme: MantineTheme): string {
  return theme.colorScheme === "dark" ? theme.white : theme.black;
}

/**
 * Returns the secondary text color
 * Used for labels, captions, and less prominent text
 *
 * @param theme - Mantine theme object
 * @returns Color string - gray[1] for dark mode, gray[4] for light mode
 */
export function getSecondaryTextColor(theme: MantineTheme): string {
  return theme.colorScheme === "dark" ? theme.colors.gray[1] : theme.colors.gray[4];
}

/**
 * Returns the muted text color
 * Used for placeholder text, disabled states, and subtle information
 *
 * @param theme - Mantine theme object
 * @returns Color string - gray[2] for dark mode, gray[6] for light mode
 */
export function getMutedTextColor(theme: MantineTheme): string {
  return theme.colorScheme === "dark" ? theme.colors.gray[2] : theme.colors.gray[6];
}

/**
 * Returns the high contrast text color
 * Used for important readable text that needs maximum legibility
 *
 * @param theme - Mantine theme object
 * @returns Color string - gray[0] for dark mode, gray[9] for light mode
 */
export function getHighContrastTextColor(theme: MantineTheme): string {
  return theme.colorScheme === "dark" ? theme.colors.gray[0] : theme.colors.gray[9];
}

/**
 * Returns the border color
 * Used for separators, card borders, and visual boundaries
 *
 * @param theme - Mantine theme object
 * @returns Color string - gray[5] for dark mode, gray[1] for light mode
 */
export function getBorderColor(theme: MantineTheme): string {
  return theme.colorScheme === "dark" ? theme.colors.gray[5] : theme.colors.gray[1];
}

/**
 * Returns the subtle border color
 * Used for more subtle borders and dividers
 *
 * @param theme - Mantine theme object
 * @returns Color string - dark[4] for dark mode, gray[1] for light mode
 */
export function getSubtleBorderColor(theme: MantineTheme): string {
  return theme.colorScheme === "dark" ? theme.colors.dark[4] : theme.colors.gray[1];
}

/**
 * Returns the surface background color
 * Used for card backgrounds, panel backgrounds, and elevated surfaces
 *
 * @param theme - Mantine theme object
 * @returns Color string - gray[7] for dark mode, gray[1] for light mode
 */
export function getSurfaceBackgroundColor(theme: MantineTheme): string {
  return theme.colorScheme === "dark" ? theme.colors.gray[7] : theme.colors.gray[1];
}

/**
 * Returns the list item background color
 * Used for list rows, selection tiles, and similar surfaces
 *
 * @param theme - Mantine theme object
 * @returns Color string - gray[8] for dark mode, gray[0] for light mode
 */
export function getListItemBackgroundColor(theme: MantineTheme): string {
  return theme.colorScheme === "dark" ? theme.colors.gray[8] : theme.colors.gray[0];
}

/**
 * Returns the hover background color
 * Used for interactive element hover states
 *
 * @param theme - Mantine theme object
 * @returns Color string - gray[6] for dark mode, gray[0] for light mode
 */
export function getHoverBackgroundColor(theme: MantineTheme): string {
  return theme.colorScheme === "dark" ? theme.colors.gray[6] : theme.colors.gray[0];
}

/**
 * Returns the page background color
 * Used for main page backgrounds and layout containers
 *
 * @param theme - Mantine theme object
 * @returns Color string - gray[8] for dark mode, #E4E4E7 for light mode
 */
export function getPageBackgroundColor(theme: MantineTheme): string {
  return theme.colorScheme === "dark" ? theme.colors.gray[8] : "#E4E4E7";
}

/**
 * Returns the base background color for cards and components
 * Used for individual card backgrounds, component backgrounds, and base surfaces
 *
 * @param theme - Mantine theme object
 * @returns Color string - gray[7] for dark mode, white for light mode
 */
export function getBaseBackgroundColor(theme: MantineTheme): string {
  return theme.colorScheme === "dark" ? theme.colors.gray[7] : "white";
}
